import { useTranslation } from 'react-i18next';
import { Button } from '@xxtract/ui';
import { ChangeChip } from './ChangeChip';
import type { ChangeDetectionResult, ProductDecision, ProductDecisionEntry } from '../intake.api';

// Technical decision identifiers (the backend's PATCH contract), not user-facing copy — kept as
// named constants so no raw string literal appears at any JSX call site (mirrors IntakeReviewPage's
// COLUMN_ACTIONS convention).
const PRODUCT_DECISIONS = {
  exclude: 'exclude',
  proceed: 'proceed',
} as const;

export interface ProductChangeListProps {
  changes: ChangeDetectionResult | undefined;
  isLoading: boolean;
  decisions: ProductDecisionEntry[];
  disabled?: boolean;
  onDecide: (gtin: string, decision: ProductDecision) => void;
}

/**
 * h5-8 (FR38a): products the change-detection service flagged as already existing get a
 * "wijziging"-chip with a diff panel (old -> new, only the fields the portal can compare) and a
 * per-product exclude/proceed choice. E5 scope is detect/show/choose only — "doorzetten" points at
 * the existing channel (B9/E7+ decides how a change is actually processed via the portal).
 */
export function ProductChangeList({ changes, isLoading, decisions, disabled, onDecide }: ProductChangeListProps) {
  const { t } = useTranslation();

  if (isLoading) return null;
  const changedProducts = (changes?.products ?? []).filter((product) => product.isChange);
  if (changedProducts.length === 0 && !changes?.degraded) return null;

  const decisionByGtin = new Map(decisions.map((entry) => [entry.gtin, entry.decision]));

  return (
    <section data-testid="intake-product-change-list" className="flex flex-col gap-3">
      {changes?.degraded ? (
        <p role="status" data-testid="intake-change-detection-degraded" className="rounded-[var(--radius-card)] border border-[var(--info)] bg-[var(--info-light)] p-3 text-sm text-[var(--info-text)]">
          {t('intake.review.changes.degraded')}
        </p>
      ) : null}

      {changedProducts.map((product) => {
        const decision = decisionByGtin.get(product.gtin);
        return (
          <div key={product.gtin} data-testid={`intake-product-change-${product.gtin}`} className="rounded-[var(--radius-card)] border border-[var(--border)] p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-[var(--foreground)]">{product.gtin}</span>
              <ChangeChip />
            </div>

            {product.scoreOnly ? (
              <p className="mt-2 text-sm text-[var(--foreground-muted)]" data-testid={`intake-product-change-scoreonly-${product.gtin}`}>
                {product.existingScore !== null
                  ? t('intake.review.changes.existsWithScore', { score: product.existingScore })
                  : t('intake.review.changes.existsUnknownScore')}
              </p>
            ) : product.diff.length > 0 ? (
              <ul className="mt-2 flex flex-col gap-1 text-sm" data-testid={`intake-product-change-diff-${product.gtin}`}>
                {product.diff.map((entry) => (
                  <li key={entry.field}>
                    <span className="font-medium">{entry.fieldLabel}</span>: {entry.oldValue} → {entry.newValue}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-[var(--foreground-muted)]">{t('intake.review.changes.noFieldDiff')}</p>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant={decision === 'exclude' ? 'default' : 'outline'}
                disabled={disabled}
                data-testid={`intake-product-exclude-${product.gtin}`}
                onClick={() => onDecide(product.gtin, PRODUCT_DECISIONS.exclude)}
              >
                {t('intake.review.changes.excludeCta')}
              </Button>
              <Button
                size="sm"
                variant={decision === 'proceed' ? 'default' : 'outline'}
                disabled={disabled}
                data-testid={`intake-product-proceed-${product.gtin}`}
                onClick={() => onDecide(product.gtin, PRODUCT_DECISIONS.proceed)}
              >
                {t('intake.review.changes.proceedCta')}
              </Button>
            </div>

            {decision === 'proceed' ? (
              <p className="mt-2 text-xs text-[var(--foreground-muted)]" data-testid={`intake-product-proceed-note-${product.gtin}`}>
                {t('intake.review.changes.proceedNote')}
              </p>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
